import { Button } from '@mui/material';
import { useFormik } from 'formik';
import React from 'react';
import * as yup from 'yup';

import { useGetFeatureFlagsQuery } from '../../../../api/features';
import { OtherIndividualInfo, otherIndividualInfoSchema, ssnFormat } from '../../../../domain/person';
import { FormikDateField } from '../../../../general/inputs/formik-date-field';
import { FormikPasswordField } from '../../../../general/inputs/formik-password-field';
import { FormikPhoneField } from '../../../../general/inputs/formik-phone-field';
import { FormikTextField } from '../../../../general/inputs/formik-text-field';
import { FormikToggle } from '../../../../general/inputs/formik-toggle';
import { removePhoneFormatting } from '../../../../utils';
import { AddressFormFields } from '../../../address/components/address-form-fields';
import { AddressFormFieldsWithAutocomplete } from '../../../address/components/address-form-fields-with-autocomplete';

const fullValidationSchema = yup.object(otherIndividualInfoSchema);
const noPhoneValidationSchema = yup.object(otherIndividualInfoSchema).omit(['phone']);

type Props = {
  person: OtherIndividualInfo;
  includePhone: boolean;
  onSubmit: (person: OtherIndividualInfo) => void;
  edit: boolean;
};

const OtherIndividualInfoForm: React.FC<Props> = ({ person, onSubmit, includePhone, edit }: Props) => {
  const { data: featureFlags } = useGetFeatureFlagsQuery();
  const validationSchema = includePhone ? fullValidationSchema : noPhoneValidationSchema;
  const formik = useFormik({
    initialValues: { ...person },
    validationSchema,
    onSubmit: (values: OtherIndividualInfo) => {
      const phone = removePhoneFormatting(values.phone);
      onSubmit({ ...values, phone });
    },
  });

  const submitLabel = edit ? 'Submit' : 'Add Owner or Executive';

  return (
    <form onSubmit={formik.handleSubmit}>
      <FormikTextField formik={formik} id="firstName" label="First Name" name="firstName" />
      <FormikTextField formik={formik} id="lastName" label="Last Name" name="lastName" />
      <FormikDateField formik={formik} id="dob" label="Date of birth" name="dob" />
      {includePhone && <FormikPhoneField formik={formik} fullWidth id="phone" label="Phone Number" name="phone" />}
      {!featureFlags?.qfEnableAddressAutocomplete && <AddressFormFields formik={formik} />}
      {featureFlags?.qfEnableAddressAutocomplete && <AddressFormFieldsWithAutocomplete formik={formik} />}
      <FormikPasswordField
        formik={formik}
        id="ssnLast4"
        label="Last 4 digits of SSN"
        name="ssnLast4"
        format={ssnFormat}
        inputProps={{ inputMode: 'numeric', maxLength: 4 }}
      />
      <FormikToggle
        name="beneficialOwner"
        formik={formik}
        id="beneficialOwner"
        label="Owns 25% or more of the company"
        toggleStyle="checkbox"
      />
      <FormikToggle
        name="controlPerson"
        formik={formik}
        id="controlPerson"
        label="Has significant management responsibility (President, CEO, CFO, VP, etc.)"
        toggleStyle="checkbox"
      />
      <Button type="submit" color="primary" variant="contained" size="large" fullWidth sx={{ mt: 4 }}>
        {submitLabel}
      </Button>
    </form>
  );
};

export { OtherIndividualInfoForm };
